// components/ImageEditor/DualFormatEditor.tsx
import React, { useState, useRef, useEffect } from "react";
import { Button } from "../ui/Button";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";

interface DualFormatEditorProps {
  image: MediaItem;
  onBack: () => void;
  onClose: () => void;
}

interface CropSettings {
  x: number;
  y: number;
  scale: number;
}

export const DualFormatEditor: React.FC<DualFormatEditorProps> = ({
  image,
  onBack,
  onClose,
}) => {
  const [cropSettings, setCropSettings] = useState<CropSettings>({
    x: 0,
    y: 0,
    scale: 1,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [activeFormat, setActiveFormat] = useState<"post" | "reel">("post");
  const [isDownloading, setIsDownloading] = useState<string | null>(null);

  const postCanvasRef = useRef<HTMLCanvasElement>(null);
  const reelCanvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Load and draw image on canvas
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (imageRef.current) {
        //@ts-ignore
        imageRef.current = img;
        drawPreviews();
      }
    };
    img.src = image.getUrlWithQueryAuth();
    //@ts-ignore
    imageRef.current = img;
  }, [image.localUrl]);

  // Redraw previews when crop settings change
  useEffect(() => {
    drawPreviews();
  }, [cropSettings]);

  const drawPreviews = () => {
    if (!imageRef.current) return;

    drawCanvas(postCanvasRef.current, 4, 5); // 4:5 ratio for posts
    drawCanvas(reelCanvasRef.current, 9, 16); // 9:16 ratio for reels
  };

  const drawCanvas = (
    canvas: HTMLCanvasElement | null,
    ratioW: number,
    ratioH: number,
  ) => {
    if (!canvas || !imageRef.current) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Calculate source dimensions based on crop settings
    const img = imageRef.current;
    const scaledWidth = img.width * cropSettings.scale;
    const scaledHeight = img.height * cropSettings.scale;

    // Calculate crop area for the specific aspect ratio
    const targetRatio = ratioW / ratioH;
    const sourceRatio = scaledWidth / scaledHeight;

    let cropWidth, cropHeight;
    if (sourceRatio > targetRatio) {
      // Image is wider than target ratio - crop horizontally
      cropHeight = scaledHeight;
      cropWidth = cropHeight * targetRatio;
    } else {
      // Image is taller than target ratio - crop vertically
      cropWidth = scaledWidth;
      cropHeight = cropWidth / targetRatio;
    }

    // Calculate centered crop position first
    const centerX = (scaledWidth - cropWidth) / 2;
    const centerY = (scaledHeight - cropHeight) / 2;

    // Apply user offset from center position
    const sourceX = Math.max(
      0,
      Math.min(scaledWidth - cropWidth, centerX + cropSettings.x),
    );
    const sourceY = Math.max(
      0,
      Math.min(scaledHeight - cropHeight, centerY + cropSettings.y),
    );

    // Draw the cropped image
    ctx.drawImage(
      img,
      sourceX / cropSettings.scale,
      sourceY / cropSettings.scale,
      cropWidth / cropSettings.scale,
      cropHeight / cropSettings.scale,
      0,
      0,
      canvasWidth,
      canvasHeight,
    );
  };

  const generateDownloadCanvas = (
    ratioW: number,
    ratioH: number,
    outputWidth: number,
  ): HTMLCanvasElement => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx || !imageRef.current) return canvas;

    // Set high-quality output dimensions
    const outputHeight = Math.round((outputWidth / ratioW) * ratioH);
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    // Calculate source dimensions based on crop settings
    const img = imageRef.current;
    const scaledWidth = img.width * cropSettings.scale;
    const scaledHeight = img.height * cropSettings.scale;

    // Calculate crop area for the specific aspect ratio
    const targetRatio = ratioW / ratioH;
    const sourceRatio = scaledWidth / scaledHeight;

    let cropWidth, cropHeight;
    if (sourceRatio > targetRatio) {
      // Image is wider than target ratio - crop horizontally
      cropHeight = scaledHeight;
      cropWidth = cropHeight * targetRatio;
    } else {
      // Image is taller than target ratio - crop vertically
      cropWidth = scaledWidth;
      cropHeight = cropWidth / targetRatio;
    }

    // Calculate centered crop position first
    const centerX = (scaledWidth - cropWidth) / 2;
    const centerY = (scaledHeight - cropHeight) / 2;

    // Apply user offset from center position
    const sourceX = Math.max(
      0,
      Math.min(scaledWidth - cropWidth, centerX + cropSettings.x),
    );
    const sourceY = Math.max(
      0,
      Math.min(scaledHeight - cropHeight, centerY + cropSettings.y),
    );

    // Draw the high-quality cropped image
    ctx.drawImage(
      img,
      sourceX / cropSettings.scale,
      sourceY / cropSettings.scale,
      cropWidth / cropSettings.scale,
      cropHeight / cropSettings.scale,
      0,
      0,
      outputWidth,
      outputHeight,
    );

    return canvas;
  };

  const downloadImage = async (format: "post" | "reel") => {
    if (!imageRef.current) return;

    setIsDownloading(format);

    try {
      let canvas: HTMLCanvasElement;
      let filename: string;

      if (format === "post") {
        // Instagram post format: 4:5 ratio, 1080x1350 pixels
        canvas = generateDownloadCanvas(4, 5, 1080);
        filename = `${image.originalFilename.replace(/\.[^/.]+$/, "")}_post_4x5.jpg`;
      } else {
        // Instagram reel format: 9:16 ratio, 1080x1920 pixels
        canvas = generateDownloadCanvas(9, 16, 1080);
        filename = `${image.originalFilename.replace(/\.[^/.]+$/, "")}_reel_9x16.jpg`;
      }

      // Convert canvas to blob and download
      canvas.toBlob(
        (blob) => {
          if (!blob) return;

          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        },
        "image/jpeg",
        0.95,
      );
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setTimeout(() => setIsDownloading(null), 1000);
    }
  };

  const downloadBothFormats = async () => {
    setIsDownloading("both");

    try {
      // Download post format
      await downloadImage("post");

      // Wait a bit and download reel format
      setTimeout(async () => {
        await downloadImage("reel");
        setIsDownloading(null);
      }, 500);
    } catch (error) {
      console.error("Batch download failed:", error);
      setIsDownloading(null);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - cropSettings.x,
      y: e.clientY - cropSettings.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !imageRef.current) return;

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    setCropSettings((prev) => ({
      ...prev,
      x: newX,
      y: newY,
    }));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleScaleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const scale = parseFloat(e.target.value);
    setCropSettings((prev) => ({ ...prev, scale }));
  };

  const handleReset = () => {
    setCropSettings({ x: 0, y: 0, scale: 1 });
  };

  const centerCrop = () => {
    setCropSettings((prev) => ({ ...prev, x: 0, y: 0 }));
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {image.originalFilename}
            </h3>
            <p className="text-sm text-gray-500">
              Create optimized copies for Instagram formats
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveFormat("post")}
                className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                  activeFormat === "post"
                    ? "bg-white text-gray-900 shadow-xs"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Post (4:5)
              </button>
              <button
                onClick={() => setActiveFormat("reel")}
                className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                  activeFormat === "reel"
                    ? "bg-white text-gray-900 shadow-xs"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Reel (9:16)
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Editor Content */}
      <div className="flex-1 flex">
        {/* Left Panel - Controls */}
        <div className="w-80 p-6 border-r bg-gray-50 space-y-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Crop & Scale</h4>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scale: {Math.round(cropSettings.scale * 100)}%
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.1"
                  value={cropSettings.scale}
                  onChange={handleScaleChange}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    X Offset
                  </label>
                  <input
                    type="number"
                    value={Math.round(cropSettings.x)}
                    onChange={(e) =>
                      setCropSettings((prev) => ({
                        ...prev,
                        x: parseInt(e.target.value) || 0,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="0 = center"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Y Offset
                  </label>
                  <input
                    type="number"
                    value={Math.round(cropSettings.y)}
                    onChange={(e) =>
                      setCropSettings((prev) => ({
                        ...prev,
                        y: parseInt(e.target.value) || 0,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="0 = center"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button variant="ghost" size="sm" onClick={centerCrop}>
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                    />
                  </svg>
                  Center
                </Button>
                <Button variant="ghost" size="sm" onClick={handleReset}>
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Reset
                </Button>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-3">Download Options</h4>
            <div className="space-y-3">
              {/* Individual Downloads */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => downloadImage("post")}
                disabled={isDownloading === "post" || isDownloading === "both"}
                isLoading={isDownloading === "post"}
                className="w-full justify-start"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download Post (4:5)
                <span className="ml-auto text-xs text-gray-500">1080×1350</span>
              </Button>

              <Button
                variant="secondary"
                size="sm"
                onClick={() => downloadImage("reel")}
                disabled={isDownloading === "reel" || isDownloading === "both"}
                isLoading={isDownloading === "reel"}
                className="w-full justify-start"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download Reel (9:16)
                <span className="ml-auto text-xs text-gray-500">1080×1920</span>
              </Button>

              {/* Batch Download */}
              <div className="pt-2 border-t">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={downloadBothFormats}
                  disabled={isDownloading !== null}
                  isLoading={isDownloading === "both"}
                  className="w-full"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                    />
                  </svg>
                  Download Both Formats
                </Button>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-3">Format Guide</h4>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="font-medium text-blue-900">
                  Post Format (4:5)
                </div>
                <div className="text-blue-700">1080 × 1350 pixels</div>
                <div className="text-blue-700">Standard Instagram post</div>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <div className="font-medium text-purple-900">
                  Reel Format (9:16)
                </div>
                <div className="text-purple-700">1080 × 1920 pixels</div>
                <div className="text-purple-700">Instagram Reels cover</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="flex-1 p-6">
          <div className="grid grid-cols-2 gap-6 h-full">
            {/* Post Preview (4:5) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">
                  Post Preview (4:5)
                </h4>
                <span className="text-sm text-gray-500">1080×1350</span>
              </div>
              <div className="relative">
                <canvas
                  ref={postCanvasRef}
                  width={320}
                  height={400}
                  className={`border-2 rounded-lg cursor-move transition-all ${
                    activeFormat === "post"
                      ? "border-blue-500 shadow-lg"
                      : "border-gray-200"
                  }`}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                />
                {activeFormat === "post" && (
                  <div className="absolute -top-2 -left-2 bg-blue-500 text-white px-2 py-1 rounded-sm text-xs font-medium">
                    Active
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Preview of how your image will look as an Instagram post
              </p>
            </div>

            {/* Reel Preview (9:16) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">Reel Cover (9:16)</h4>
                <span className="text-sm text-gray-500">1080×1920</span>
              </div>
              <div className="relative flex justify-center">
                <canvas
                  ref={reelCanvasRef}
                  width={225}
                  height={400}
                  className={`border-2 rounded-lg cursor-move transition-all ${
                    activeFormat === "reel"
                      ? "border-purple-500 shadow-lg"
                      : "border-gray-200"
                  }`}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                />
                {activeFormat === "reel" && (
                  <div className="absolute -top-2 -left-2 bg-purple-500 text-white px-2 py-1 rounded-sm text-xs font-medium">
                    Active
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Preview of how your image will look as a reel cover
              </p>
            </div>
          </div>

          {/* Download Status */}
          {isDownloading && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2 text-green-800">
                <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                <div>
                  <p className="font-medium">
                    {isDownloading === "both"
                      ? "Downloading both formats..."
                      : isDownloading === "post"
                        ? "Downloading post format..."
                        : "Downloading reel format..."}
                  </p>
                  <p className="text-sm text-green-600">
                    Your optimized images will be saved to your Downloads folder
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <svg
                className="w-5 h-5 text-yellow-600 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-sm">
                <p className="font-medium text-yellow-800">
                  Centered Cropping:
                </p>
                <ul className="text-yellow-700 mt-1 space-y-1">
                  <li>• Image crops equally from both sides by default</li>
                  <li>• X/Y offset = 0 means perfectly centered</li>
                  <li>• Drag to adjust position or use the Center button</li>
                  <li>• Scale to zoom in/out while maintaining center crop</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
